import _ from 'lodash';
import { useMemo } from 'react';
import { AMP_SETTINGS } from '@ampsec/platform-client';
import { EARLY_ACCESS_ASSISTANT_TIER } from 'Core-utils/constants/constants';
import { useTenantSettings } from './useTenantSettings';

export type EarlyAccessTier = 'DEV' | 'BETA' | 'STABLE' | null;

export interface EarlyAccessState {
    isLoading: boolean;
    isEarlyAccessEnabled: boolean;
    isAmpyEarlyAccessEnabled: boolean;
    tier: EarlyAccessTier;
    ampyTier: EarlyAccessTier;
    /** Convenience flag representing: early access enabled AND tier is DEV or BETA */
    isDevTier: boolean;
    /** Convenience flag representing: early access enabled AND tier is DEV or BETA */
    isBetaTier: boolean;
    /** Convenience flag representing: ampy early access enabled AND tier is DEV or BETA */
    isAmpyBetaTier: boolean;
    /** Convenience flag representing: ampy early access enabled AND tier is DEV or BETA */
    isAmpyDevTier: boolean;
}

/**
 * Hook for checking tenant early-access status and tier.
 *
 * @example
 * // Simple feature gating
 * const { isLoading, isDevTier } = useEarlyAccess();
 * if (isLoading) return <Spinner />;
 * if (isDevTier) { bleedingEdgeFeature(); }
 *
 */
export const useEarlyAccess = (): EarlyAccessState => {
    const { tenantSettings, getAmpSettingValue, settingsLoading } = useTenantSettings();

    return useMemo(() => {
        const isLoading = settingsLoading || !tenantSettings || Object.keys(tenantSettings).length === 0;

        if (isLoading) {
            return {
                isLoading: true,
                isEarlyAccessEnabled: false,
                tier: null,
                ampyTier: null,
                isDevTier: false,
                isBetaTier: false,
                isAmpyEarlyAccessEnabled: false,
                isAmpyBetaTier: false,
                isAmpyDevTier: false,
            };
        }

        const earlyAccessEnabled: boolean =
          (getAmpSettingValue(AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.id) ?? AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.defaultValue) !== AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.defaultValue;
        const tier: EarlyAccessTier =
          (getAmpSettingValue(AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.id) ?? AMP_SETTINGS.tenant.EARLY_ACCESS_TIER.defaultValue) as EarlyAccessTier;
        const ampyEarlyAccessEnabled: boolean =
          (getAmpSettingValue(AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.id) ?? AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.defaultValue) !== AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.defaultValue;
        const ampyTier: EarlyAccessTier =
          (getAmpSettingValue(AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.id) ?? AMP_SETTINGS.tenant.AMPY_EARLY_ACCESS_TIER.defaultValue) as EarlyAccessTier;

        return {
            isLoading: false,
            isEarlyAccessEnabled: earlyAccessEnabled,
            isAmpyEarlyAccessEnabled: ampyEarlyAccessEnabled,
            ampyTier: ampyTier as EarlyAccessTier,
            tier: tier as EarlyAccessTier,
            isDevTier: earlyAccessEnabled && tier === EARLY_ACCESS_ASSISTANT_TIER.DEV,
            isBetaTier: earlyAccessEnabled && tier === EARLY_ACCESS_ASSISTANT_TIER.BETA,
            isAmpyBetaTier: ampyEarlyAccessEnabled && ampyTier === EARLY_ACCESS_ASSISTANT_TIER.BETA,
            isAmpyDevTier: ampyEarlyAccessEnabled && ampyTier === EARLY_ACCESS_ASSISTANT_TIER.DEV,
        };
    }, [tenantSettings, settingsLoading, getAmpSettingValue]);
};
